import './styles/gamepage.css';
import Appbar from '../components/Appbar';
import Board from '../components/Board';
import Swaps from '../components/Swaps';
import NextLetters from '../components/NextLetters';
import Score from '../components/Score';
import WordBox from '../components/WordBox';
import Timer from '../components/Timer';
import { useGameStore } from '../../store/gameStore';
import { useShallow } from 'zustand/react/shallow';
import { useEffect, useState } from 'react';
import { getStarCount } from '../utils/scoreResultHelper';
import GameIntroDialog from '../components/dialogs/GameIntroDialog';
import { applyEndGameAnimation } from '../utils/animationHelper';
import GameOverDialog from '../components/dialogs/GameOverDialog';
import { writeToLeaderboard } from '../utils/leaderboardHelper';
import { colorUnlockables } from '../utils/unlockablesHelper';
import { toast } from 'react-hot-toast';
import CustomToast from '../subcomponents/Toast';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { GameMode } from '@/types/types';
import { syncStats } from '../utils/syncHelper';

function GamePage() {
  // Subscribe to game store
  const {
    mode,
    gameEnded,
    starsAdded,
    moveCount,
    submittedScore,
    unlockedColors,
    recentFoundWords,
    setPauseActions,
    setStarsAdded,
    increaseStarCount,
    addUnlockedColor,
  } = useGameStore(
    useShallow((state) => ({
      mode: state.mode,
      gameEnded: state.gameData[state.mode].gameEnded,
      starsAdded: state.gameData[state.mode].starsAdded,
      moveCount: state.gameData[state.mode].moveCount,
      submittedScore: state.gameData[state.mode].submittedScore,
      unlockedColors: state.unlockedColors,
      recentFoundWords: state.gameData[state.mode].recentFoundWords,
      setPauseActions: state.setPauseActions,
      updateStats: state.updateStats,
      setStarsAdded: state.setStarsAdded,
      increaseStarCount: state.increaseStarCount,
      addUnlockedColor: state.addUnlockedColor,
    }))
  );

  const showTimer = () => {
    return mode.includes('blitz');
  };

  const [showGameOver, setShowGameOver] = useState<boolean>(false);
  const [showGameIntro, setShowGameIntro] = useState<boolean>(false);

  // Show game intro if game has not started
  useEffect(() => {
    if (moveCount === 0) {
      setPauseActions(true);
      setTimeout(() => {
        setShowGameIntro(true);
        setPauseActions(false);
      }, 200);
    }
  }, []);

  // Animate the board when game is over / Show game over dialog
  useEffect(() => {
    if (gameEnded) {
      setPauseActions(true);
      applyEndGameAnimation(mode.includes('simple') ? 4 : 5);
      setTimeout(() => {
        setShowGameOver(true);
        setPauseActions(false);
      }, 1000);
    }
  }, [gameEnded]);

  // Update stats / star count when game ends / show toast message if unlock
  useEffect(() => {
    if (gameEnded) {
      const { newHighScore, mode } = useGameStore.getState().updateStats();

      const state = useGameStore.getState();

      // Try to sync stats to supabase so user can recover
      syncStats({
        stars: state.stars,
        gameStats: state.gameStats,
        unlockedColors: state.unlockedColors,
      });

      const getTitle = (mode: GameMode) => {
        switch (mode) {
          case 'blitzSimple':
            return '4x4 BLITZ';
          case 'blitzDeluxe':
            return '5x5 BLITZ';
          case 'endlessSimple':
            return '4x4 ENDLESS';
          case 'endlessDeluxe':
            return '5x5 ENDLESS';
        }
      };

      if (newHighScore) {
        setTimeout(() => {
          toast.custom(
            (t) => <CustomToast t={t} message={`New high score in ${getTitle(mode)}`} star />,
            { duration: 3000 }
          );
        }, 1500);
      }
    }

    if (gameEnded && !starsAdded) {
      const starsEarned = getStarCount();
      const prevStars = useGameStore.getState().stars;
      const newStars = prevStars + starsEarned;

      colorUnlockables.forEach(({ color, unlockAmount, customUnlockCheck, hideToast }) => {
        const isAlreadyUnlocked = unlockedColors.includes(color);

        const unlockedNow =
          unlockAmount !== undefined
            ? prevStars < unlockAmount && newStars >= unlockAmount
            : customUnlockCheck?.() && !isAlreadyUnlocked;

        if (unlockedNow && !isAlreadyUnlocked && !hideToast) {
          setTimeout(() => {
            toast.custom(
              (t) => (
                <CustomToast t={t} message={`${color.toUpperCase()} animation unlocked`} star />
              ),
              { duration: 3000 }
            );
          }, 1600);
          addUnlockedColor(color);
        }
      });

      increaseStarCount(starsEarned);
      setStarsAdded();
    }
  }, [gameEnded]);

  // Try to submit to leaderboard
  useEffect(() => {
    if (!submittedScore && gameEnded) writeToLeaderboard();
  }, [gameEnded]);

  // Confetti effect
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const { width, height } = useWindowSize();
  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      return;
    }
    if (recentFoundWords.includes('party')) {
      setShowConfetti(true);
      const timeout = setTimeout(() => setShowConfetti(false), 10000);
      return () => clearTimeout(timeout);
    }
  }, [recentFoundWords]);

  return (
    <div className="game-wrapper">
      <Appbar />
      <div className="board-wrapper">
        <span className="top-hud">
          <Swaps />
          {showTimer() && <Timer />}
        </span>
        <span className="bottom-hud">
          <Score />
          <NextLetters />
        </span>
        <Board setShowGameOver={setShowGameOver} />
      </div>
      <WordBox />
      <GameIntroDialog
        open={showGameIntro}
        onClose={() => {
          setShowGameIntro(false);
        }}
      />
      <GameOverDialog open={showGameOver} onClose={() => setShowGameOver(false)} />
      {/* Special Effect */}
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={120}
          gravity={0.2}
          recycle={false}
        />
      )}
    </div>
  );
}

export default GamePage;
